import { useMemo, useRef, useState } from 'react';
import { useIntl } from 'umi';
import {
  Bubble,
  Button,
  Checkbox,
  Icon,
  Input,
  List,
  Radio,
  StatusTip,
  Tag,
} from 'tea-component';
import React from 'react';
import BubbleCommon from '../../../BubbleCommon';
import { escapeAllTags, keywordRenderFunc } from '@/pages/opinion/utils/text';
import { NO_DATE } from '@/pages/opinion/constants';
import './index.scss';

type VagueSearchSelectedType = {
  changeInputRef: (value: any) => void;
  values: Array<string>;
  vagueValues: Array<string>;
  excludeValues: Array<string>;
  getFilterList: any;
  loading: boolean;
  basicOptions: { text: string; value: string; disabled?: boolean }[];
  setLoading: any;
  onFocus: any;
  setValues: any;
  setVagueValues: any;
  setExcludeValues: any;
  setKeyword?: React.Dispatch<React.SetStateAction<string>>;
  allFilterTips?: any;
  loadingText?: any;
  placeholderText?: any;
  topTips?: any;
  mainText?: any;
  footerElement?: (value: { searchValue: string; className: string }) => any;
  total?: number;
  searchListFooterText?: string;
};

export default function VagueSearchSelected({
  changeInputRef,
  values = [],
  excludeValues = [],
  vagueValues = [],
  setExcludeValues,
  setVagueValues,
  getFilterList,
  loading,
  basicOptions,
  setLoading,
  onFocus,
  setValues,
  setKeyword,
  allFilterTips = null,
  loadingText = NO_DATE.EMPTY,
  placeholderText = NO_DATE.EMPTY,
  topTips = NO_DATE.EMPTY,
  mainText = NO_DATE.EMPTY,
  footerElement,
  total = 0,
  searchListFooterText,
}: VagueSearchSelectedType) {
  const [value, setValue] = useState('');
  const { formatMessage } = useIntl();
  const options = useMemo(() => {
    return basicOptions;
  }, [basicOptions]);

  const status = useMemo(() => {
    if (loading) {
      return 'loading';
    }
    if (options.length === 0) {
      return 'empty';
    }

    return false;
  }, [loading, options]);

  function onFocusInput() {
    setTimeout(onFocus, 10);
  }

  const changeAllSelected = () => {
    onFocusInput();
    if (vagueValues.includes(value)) {
      setVagueValues(vagueValues.filter((item) => item !== value));
      return;
    }

    const list = values.filter((item) => !item.includes(value));
    setValues(list);
    setVagueValues([...vagueValues, value]);
  };

  const deleteTag = (tag: string, type: 'exclude' | 'all' | 'common') => {
    const tagsMap = {
      common: { value: values, setFunc: setValues },
      all: { value: vagueValues, setFunc: setVagueValues },
      exclude: { value: excludeValues, setFunc: setExcludeValues },
    };

    const { value, setFunc } = tagsMap[type];
    const list = value.filter((item: any) => item !== tag);
    setFunc(list);
  };

  function isSelected(value: string) {
    // 排除的先去掉
    if (excludeValues.includes(value)) return false;

    // 模糊匹配
    if (
      vagueValues.some((item) => {
        const info = value.toLocaleLowerCase();
        const keyword = item.toLocaleLowerCase();
        return info.includes(keyword);
      })
    )
      return true;

    // 选项进行匹配
    return values.includes(value);
  }

  function selectCheckedValue(value: string) {
    if (excludeValues.includes(value)) {
      const flag = vagueValues.some((item) => value.includes(item));

      setExcludeValues(excludeValues.filter((item) => item !== value));

      if (flag) return;
    }

    setValues([...values, value]);
  }

  function cancelCheckedValue(value: string) {
    if (values.includes(value)) {
      return setValues(values.filter((item) => item !== value));
    }

    setExcludeValues([...excludeValues, value]);
  }

  function changeCheckbox(value: boolean, selected: string) {
    const func = value ? selectCheckedValue : cancelCheckedValue;
    func(selected);
    onFocusInput();
  }

  function renderTagList(
    tags: Array<string>,
    type: 'exclude' | 'all' | 'common' = 'common',
  ) {
    const tagsRenderMap = {
      exclude: (text: string) => formatMessage({ id: 'Exclude' }) + ': ' + text,
      all: (text: string) => text + ': ' + formatMessage({ id: 'Select All' }),
      common: (text: string) => text,
    };
    const render = tagsRenderMap[type];
    return tags.map((item: string) => (
      <Tag
        key={item}
        className="dbo-tag-item"
        theme="default"
        onClose={() => deleteTag(item, type)}
      >
        {render(item)}
      </Tag>
    ));
  }

  function renderExcludeTagList(tags: Array<string>) {
    if (tags.length < 3) {
      return renderTagList(excludeValues, 'exclude');
    }

    return (
      <Bubble
        content={
          <div>
            <p>{formatMessage({ id: 'Exclude' })}:</p>
            {tags.join(', ')}
          </div>
        }
      >
        <Tag
          className="dbo-tag-item"
          theme="default"
          onClose={() => setExcludeValues([])}
        >
          {formatMessage({ id: 'Exclude' })}: {tags.join(', ')}
        </Tag>
      </Bubble>
    );
  }

  return (
    <div className="vague-search-selected-wrapper">
      <div className="dbo-search-input-box">
        <div className="dbo-search-input-info">
          {renderExcludeTagList(excludeValues)}
          {renderTagList(vagueValues, 'all')}
          {renderTagList(values, 'common')}
          <Input
            className={`dbo-search-input`}
            value={value}
            ref={changeInputRef}
            placeholder={formatMessage({ id: placeholderText })}
            onChange={(text) => {
              setValue(text);
              setLoading(true);
              getFilterList(text);
              setKeyword?.(value);
            }}
          />
        </div>
        <div className="dbo-select-search-icon">
          <Icon
            type="dismiss"
            className={`icon-info before ${
              values.length > 0 || value ? 'show' : 'none'
            }`}
            onClick={() => {
              setValue('');
              getFilterList();
            }}
          />
          <Icon
            type="search"
            className="icon-info"
            onClick={() => getFilterList(value)}
          />
        </div>
      </div>

      {status ? (
        <StatusTip
          className="loading-text"
          loadingText={formatMessage({ id: loadingText })}
          status={status}
        />
      ) : (
        <>
          <div className="top-tips">{formatMessage({ id: topTips })}</div>
          {!value && (
            <div className="radio-all-box">
              <Radio
                name="all"
                className="radio-item"
                onClick={() => {
                  setValues([]);
                  setVagueValues([]);
                  setExcludeValues([]);
                }}
                value={
                  [...values, ...vagueValues, ...excludeValues].length === 0
                }
              >
                {formatMessage({ id: 'All' })}
                {allFilterTips && (
                  <BubbleCommon placement="top" tipsMap={allFilterTips} />
                )}
              </Radio>
            </div>
          )}
          <div className="title-box">
            <label className="label-title">
              {value
                ? `${formatMessage({ id: 'Search Result' })} (${total})`
                : formatMessage({ id: mainText })}
            </label>
            {value && (
              <Button onClick={changeAllSelected} type="link">
                {formatMessage({
                  id: vagueValues.includes(value) ? 'Clear All' : 'Select All',
                })}
              </Button>
            )}
          </div>
          <div className="dbo-list-box">
            <List type="option" className="select-list-box">
              {options.map(({ text, value: optionsValue, disabled }) => {
                const isSelectedValue = isSelected(optionsValue);
                return (
                  <List.Item
                    onClick={() =>
                      changeCheckbox(!isSelectedValue, optionsValue)
                    }
                    key={optionsValue}
                    className="select-list-item"
                  >
                    <Checkbox
                      disabled={disabled}
                      className="check-box-item"
                      name={value}
                      onChange={(value) => changeCheckbox(value, optionsValue)}
                      value={isSelectedValue}
                    >
                      {keywordRenderFunc({
                        text: escapeAllTags(text),
                        keyword: value,
                      })}
                    </Checkbox>
                  </List.Item>
                );
              })}
              {value && searchListFooterText && (
                <div className="dbo-footer-element-tips">
                  {formatMessage({ id: searchListFooterText })}
                </div>
              )}
            </List>
          </div>
        </>
      )}

      {status !== 'loading' &&
        footerElement?.({
          searchValue: value,
          className: 'dbo-footer-element-tips',
        })}
    </div>
  );
}
